import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import Editor from '@monaco-editor/react'
import { API, practiceApi, errorMessage } from './api'
import { runInBrowser, warmUpRunner } from './browserRunner'
import { PRACTICE_PATH, LANGUAGE_LABELS, LEVEL_LABELS, preferredLanguage, rememberLanguage, WorkedExample } from './practiceShared'
import './stDiagnostic.css'

const AUTOSAVE_MS = 2000
// Active-time tracking: a second only counts while this tab is visible and focused and the
// learner did something (typed, moved the mouse, scrolled) within the last IDLE_AFTER_MS.
const TICK_MS = 5000
const IDLE_AFTER_MS = 60000
const TIME_REPORT_MS = 30000
const ACTIVITY_EVENTS = ['keydown', 'mousemove', 'mousedown', 'wheel', 'scroll', 'touchstart']

// One practice problem: write the plan, code it, run it on the samples, submit it against
// the hidden tests (run here in the browser, checked on the server).
export default function StPracticeProblem() {
  const { problemId } = useParams()
  const [state, setState] = useState('loading')
  const [error, setError] = useState('')
  const [problem, setProblem] = useState(null)
  const [pattern, setPattern] = useState(null)
  const [progress, setProgress] = useState(null)
  const [solution, setSolution] = useState(null)
  const [minPlanLength, setMinPlanLength] = useState(30)
  const [language, setLanguage] = useState(preferredLanguage)
  const [code, setCode] = useState({})
  const [planDraft, setPlanDraft] = useState('')
  const [editingPlan, setEditingPlan] = useState(false)
  const [planMsg, setPlanMsg] = useState('')
  const [showPattern, setShowPattern] = useState(false)
  const [customInput, setCustomInput] = useState('')
  const [useCustom, setUseCustom] = useState(false)
  const [busy, setBusy] = useState('')
  const [runResult, setRunResult] = useState(null)
  const [submitResult, setSubmitResult] = useState(null)
  const [actionError, setActionError] = useState('')
  const [showSolution, setShowSolution] = useState(false)

  const dirtyRef = useRef(false)
  const codeRef = useRef(code)
  codeRef.current = code

  const load = useCallback(async () => {
    try {
      const { data } = await practiceApi.get(`/problems/${problemId}`)
      setProblem(data.problem)
      setPattern(data.pattern)
      setProgress(data.progress)
      setSolution(data.solution)
      setMinPlanLength(data.minPlanLength)
      setPlanDraft(data.progress.plan || '')
      setCode({ ...data.problem.starter, ...(data.progress.code || {}) })
      if (data.progress.language) setLanguage(data.progress.language)
      setState('ready')
    } catch (err) {
      if (err?.response?.status === 401) setState('login')
      else { setError(errorMessage(err)); setState('error') }
    }
  }, [problemId])

  useEffect(() => { load() }, [load])
  useEffect(() => { warmUpRunner(language) }, [language])

  // ─── Active time ───────────────────────────────────────────────────────────
  const pendingSecondsRef = useRef(0)

  const reportTime = useCallback(async ({ keepalive = false } = {}) => {
    const seconds = Math.round(pendingSecondsRef.current)
    if (seconds < 1) return
    pendingSecondsRef.current = 0
    try {
      if (keepalive) {
        // The page is closing: fetch with keepalive can finish after the page is gone.
        await fetch(`${API}/api/st-practice/problems/${problemId}/time`, {
          method: 'POST', keepalive: true, credentials: 'include',
          headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: JSON.stringify({ seconds })
        })
      } else {
        await practiceApi.post(`/problems/${problemId}/time`, { seconds })
      }
    } catch {
      pendingSecondsRef.current += seconds // try again with the next report
    }
  }, [problemId])

  useEffect(() => {
    let lastActivity = Date.now()
    const markActive = () => { lastActivity = Date.now() }
    ACTIVITY_EVENTS.forEach(e => window.addEventListener(e, markActive, { capture: true, passive: true }))
    const tick = setInterval(() => {
      const active = document.visibilityState === 'visible' && document.hasFocus() && Date.now() - lastActivity < IDLE_AFTER_MS
      if (active) pendingSecondsRef.current += TICK_MS / 1000
    }, TICK_MS)
    const report = setInterval(() => reportTime(), TIME_REPORT_MS)
    const onHide = () => { if (document.visibilityState === 'hidden') reportTime({ keepalive: true }) }
    document.addEventListener('visibilitychange', onHide)
    return () => {
      ACTIVITY_EVENTS.forEach(e => window.removeEventListener(e, markActive, { capture: true }))
      clearInterval(tick)
      clearInterval(report)
      document.removeEventListener('visibilitychange', onHide)
      reportTime({ keepalive: true })
    }
  }, [reportTime])

  // Autosave the code for the current language.
  const saveCode = useCallback(async () => {
    if (!dirtyRef.current) return
    dirtyRef.current = false
    try {
      await practiceApi.put(`/problems/${problemId}/code`, { language, code: codeRef.current[language] })
    } catch {
      dirtyRef.current = true
    }
  }, [problemId, language])

  useEffect(() => {
    const timer = setInterval(saveCode, AUTOSAVE_MS)
    return () => { clearInterval(timer); saveCode() }
  }, [saveCode])

  if (state === 'loading') return <div className="std"><div className="std-wrap">Loading…</div></div>
  if (state === 'login') return <div className="std"><div className="std-wrap"><div className="std-card"><h2>Please sign in</h2><Link className="std-btn" to="/login">Sign in</Link></div></div></div>
  if (state === 'error') return <div className="std"><div className="std-wrap"><div className="std-alert std-alert-bad">{error}</div><Link to={PRACTICE_PATH}>← All problems</Link></div></div>

  const hasPlan = !!progress.plan
  const currentCode = code[language] ?? problem.starter[language]

  const changeLanguage = async (l) => {
    await saveCode()
    setLanguage(l)
    rememberLanguage(l)
    setRunResult(null)
  }

  const setCurrentCode = (value) => {
    setCode(prev => ({ ...prev, [language]: value }))
    dirtyRef.current = true
  }

  const savePlan = async () => {
    setPlanMsg('')
    try {
      await practiceApi.put(`/problems/${problemId}/plan`, { plan: planDraft })
      setProgress(prev => ({ ...prev, plan: planDraft.trim() }))
      setEditingPlan(false)
    } catch (err) {
      setPlanMsg(errorMessage(err))
    }
  }

  const run = async () => {
    setBusy('run')
    setActionError('')
    setRunResult(null)
    const sample = problem.samples[0]
    const input = useCustom ? customInput : sample.input
    const r = await runInBrowser(language, currentCode, input)
    const lines = (r.output || '').replace(/\r/g, '').split('\n').map(l => l.trim()).filter(Boolean)
    const answer = lines[lines.length - 1] || ''
    setRunResult({ ...r, input, expected: useCustom ? null : sample.expected, matches: useCustom || r.error ? null : answer === sample.expected })
    setBusy('')
  }

  const submit = async () => {
    setBusy('submit')
    setActionError('')
    setSubmitResult(null)
    try {
      await saveCode()
      // Count the time up to now, so "time to solve" includes this attempt.
      await reportTime()
      const { data } = await practiceApi.get(`/problems/${problemId}/tests`)
      const outputs = []
      for (let i = 0; i < data.inputs.length; i++) {
        setBusy(`submit ${i + 1}/${data.inputs.length}`)
        const r = await runInBrowser(language, currentCode, data.inputs[i])
        if (r.loadFailed) throw new Error(`${LANGUAGE_LABELS[language]} could not run in this browser: ${r.error}`)
        outputs.push(r.output || r.error || '')
      }
      const { data: result } = await practiceApi.post(`/problems/${problemId}/submit`, { language, code: currentCode, outputs })
      setSubmitResult(result)
      setProgress(result.progress)
      if (result.solution) setSolution(result.solution)
    } catch (err) {
      setActionError(errorMessage(err))
    } finally {
      setBusy('')
    }
  }

  const openHint = async () => {
    setActionError('')
    try {
      const { data } = await practiceApi.post(`/problems/${problemId}/hint`)
      setProblem(prev => ({ ...prev, hints: data.hints }))
      setProgress(data.progress)
    } catch (err) {
      setActionError(errorMessage(err))
    }
  }

  // Unique labels of the hidden tests, in order, with whether each passed.
  const resultLabels = submitResult ? Object.values(submitResult.results.reduce((acc, r) => {
    acc[r.label] = acc[r.label] ? { label: r.label, pass: acc[r.label].pass && r.pass } : { ...r }
    return acc
  }, {})) : []

  return (
    <div className="std">
      <div className="std-wrap">
        <Link to={PRACTICE_PATH} className="std-small">← All problems</Link>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 10, marginTop: 8 }}>
          <h1 style={{ margin: 0 }}>{problem.title}</h1>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <span className="std-badge">{LEVEL_LABELS[problem.level]} · {pattern.title}</span>
            {progress.solved && <span className="std-badge std-badge-ok">{progress.solvedOnOwn ? '✓ Solved on your own' : '✓ Solved with hints'}</span>}
          </div>
        </div>

        <div className="std-card" style={{ marginTop: 12 }}>
          <p style={{ marginTop: 0 }}>{problem.statement}</p>
          <p className="std-muted std-small" style={{ margin: '4px 0' }}><strong>Input:</strong> {problem.inputFormat}</p>
          <div className="std-grid-2">
            {problem.samples.map((s, i) => (
              <div key={i}>
                <div className="std-small std-muted">Sample {i + 1}: input → expected output</div>
                <pre className="std-pre std-pre-light">{s.input || '(empty)'}{'\n'}→ {s.expected}</pre>
              </div>
            ))}
          </div>
          <button className="std-btn std-btn-ghost std-btn-sm" onClick={() => setShowPattern(v => !v)}>
            {showPattern ? 'Hide the pattern' : `Pattern: ${pattern.title} — show the idea and a worked example`}
          </button>
          {showPattern && (
            <div style={{ marginTop: 10 }}>
              <p className="std-small" style={{ margin: '0 0 6px' }}>{pattern.idea}</p>
              <WorkedExample example={pattern.example} language={language} />
            </div>
          )}
        </div>

        <div className="std-card" style={{ background: hasPlan && !editingPlan ? 'var(--std-ok-soft)' : 'var(--std-accent-soft)', borderColor: 'transparent' }}>
          <h3 style={{ marginBottom: 4 }}>Step 1 — Your plan</h3>
          {hasPlan && !editingPlan ? (
            <>
              <p style={{ whiteSpace: 'pre-wrap', margin: '4px 0' }}>{progress.plan}</p>
              <button className="std-btn std-btn-ghost std-btn-sm" onClick={() => { setPlanDraft(progress.plan); setEditingPlan(true) }}>Edit plan</button>
            </>
          ) : (
            <>
              <p className="std-small std-muted" style={{ margin: '0 0 8px' }}>
                Before coding, write the steps you will follow in plain English. Then work through Sample 1 by hand with those steps.
              </p>
              <textarea value={planDraft} onChange={e => setPlanDraft(e.target.value)}
                placeholder={'1. Start with ...\n2. For each ...\n3. Return ...'} />
              {planMsg && <div className="std-alert std-alert-bad">{planMsg}</div>}
              <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginTop: 8 }}>
                <button className="std-btn" disabled={planDraft.trim().length < minPlanLength} onClick={savePlan}>Save plan</button>
                {editingPlan && <button className="std-btn std-btn-ghost" onClick={() => setEditingPlan(false)}>Cancel</button>}
                <span className="std-small std-muted">{planDraft.trim().length < minPlanLength ? `${minPlanLength - planDraft.trim().length} more characters` : 'Ready'}</span>
              </div>
            </>
          )}
        </div>

        <div className="std-card">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, marginBottom: 8 }}>
            <h3 style={{ margin: 0 }}>Step 2 — Code {!hasPlan && <span className="std-muted std-small" style={{ fontWeight: 400 }}>(unlocks after you save your plan)</span>}</h3>
            <select value={language} onChange={e => changeLanguage(e.target.value)} style={{ width: 'auto' }} disabled={!!busy}>
              {Object.entries(LANGUAGE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
          </div>
          <p className="std-small std-muted" style={{ margin: '0 0 8px' }}>Write only the function at the top. The part below it reads the input and prints your answer.</p>
          <div className={`std-editor ${hasPlan ? '' : 'std-editor-locked'}`}>
            <Editor height="380px" language={language} value={currentCode} onChange={v => setCurrentCode(v ?? '')}
              options={{ readOnly: !hasPlan, minimap: { enabled: false }, fontSize: 14, scrollBeyondLastLine: false, tabSize: language === 'python' ? 4 : 2 }} />
          </div>

          <div style={{ marginTop: 10 }}>
            <label className="std-small" style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
              <input type="checkbox" checked={useCustom} onChange={e => setUseCustom(e.target.checked)} disabled={!hasPlan} />
              Run with my own input instead of Sample 1
            </label>
            {useCustom && <textarea style={{ minHeight: 70, fontFamily: 'monospace', marginTop: 6 }} value={customInput} onChange={e => setCustomInput(e.target.value)} />}
            <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginTop: 8, flexWrap: 'wrap' }}>
              <button className="std-btn std-btn-ghost" onClick={run} disabled={!hasPlan || !!busy}>
                {busy === 'run' ? (language === 'python' ? 'Running… (first run loads Python)' : 'Running…') : '▶ Run'}
              </button>
              <button className="std-btn" onClick={submit} disabled={!hasPlan || !!busy}>
                {busy.startsWith('submit') ? `Testing… ${busy.slice(7)}` : `Submit (${problem.testCount} hidden tests)`}
              </button>
              <span className="std-small std-muted">{progress.submissions ? `${progress.submissions} submission(s) so far` : ''}</span>
            </div>
          </div>

          {actionError && <div className="std-alert std-alert-bad">{actionError}</div>}

          {runResult && (
            <div style={{ marginTop: 10 }}>
              {runResult.error && <div className="std-alert std-alert-bad">{runResult.error}</div>}
              {runResult.matches !== null && (
                <div className={`std-alert ${runResult.matches ? 'std-alert-ok' : 'std-alert-warn'}`}>
                  {runResult.matches ? 'Matches Sample 1. Now submit to check the hidden tests.' : `Does not match Sample 1 (expected ${runResult.expected}).`}
                </div>
              )}
              {(runResult.output || !runResult.error) && <pre className="std-pre std-output">{runResult.output || '(no output)'}</pre>}
            </div>
          )}

          {submitResult && (
            <div style={{ marginTop: 12 }}>
              <div className={`std-alert ${submitResult.solved ? 'std-alert-ok' : 'std-alert-warn'}`}>
                <strong>{submitResult.passed}/{submitResult.total} hidden tests passed.</strong>{' '}
                {submitResult.solved
                  ? (submitResult.firstSolve ? (submitResult.solvedOnOwn ? 'Solved on your own — well done!' : 'Solved — next time try without hints.') : 'Still solved.')
                  : 'Look at which kinds of input fail, work one through by hand with your plan, then fix the code.'}
              </div>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                {resultLabels.map(r => <span key={r.label} className={`std-badge ${r.pass ? 'std-badge-ok' : 'std-badge-bad'}`}>{r.pass ? '✓' : '✗'} {r.label}</span>)}
              </div>
            </div>
          )}
        </div>

        {!progress.solved && problem.hintCount > 0 && (
          <div className="std-card">
            <h3 style={{ marginBottom: 6 }}>Hints <span className="std-muted std-small" style={{ fontWeight: 400 }}>({progress.hintsUsed}/{problem.hintCount} opened)</span></h3>
            <p className="std-small std-muted" style={{ margin: '0 0 8px' }}>
              Each hint takes you one step closer to the answer. Try first; problems solved without hints count as "solved on your own".
            </p>
            {problem.hints.map((h, i) => (
              <div key={i} className="std-alert std-alert-info">
                <strong>Hint {i + 1} — {h.title}</strong>
                <div style={{ whiteSpace: 'pre-wrap', marginTop: 4 }}>{h.text}</div>
                {h.code && <pre className="std-pre" style={{ marginBottom: 0 }}>{h.code[language]}</pre>}
              </div>
            ))}
            {progress.hintsUsed < problem.hintCount && (
              progress.hintAvailable
                ? <button className="std-btn std-btn-ghost std-btn-sm" onClick={openHint}>Show hint {progress.hintsUsed + 1} — {problem.hintTitles[progress.hintsUsed]}</button>
                : <p className="std-small std-muted" style={{ margin: 0 }}>
                    Hint {progress.hintsUsed + 1} — {problem.hintTitles[progress.hintsUsed]} opens after {progress.failuresUntilNextHint === 1 && progress.failedSubmissions === 0 ? 'your first wrong submission' : `${progress.failuresUntilNextHint} more wrong submission(s)`}.
                  </p>
            )}
          </div>
        )}

        {progress.solved && solution && (
          <div className="std-card">
            <h3 style={{ marginBottom: 6 }}>Compare with a model solution</h3>
            <p className="std-small std-muted" style={{ margin: '0 0 8px' }}>Yours may be different and still correct. Look for a simpler step or a case you handled differently.</p>
            <button className="std-btn std-btn-ghost std-btn-sm" onClick={() => setShowSolution(v => !v)}>{showSolution ? 'Hide' : 'Show'} model solution ({LANGUAGE_LABELS[language]})</button>
            {showSolution && <pre className="std-pre">{solution[language]}</pre>}
          </div>
        )}
      </div>
    </div>
  )
}
